import React, { useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, X, Upload, Trash2, Loader2 } from "lucide-react";

export const EMPTY_MERCH = {
  name: "", description: "", price: "", image_url: "", requires_shipping: true,
  stock: "", variants: [], active: true,
};

export const merchToForm = (it) => ({
  name: it.name || "", description: it.description || "",
  price: ((it.price_cents || 0) / 100).toFixed(2), image_url: it.image_url || "",
  requires_shipping: it.requires_shipping !== false,
  stock: it.stock == null ? "" : String(it.stock),
  variants: (it.variants || []).map((v) => ({ label: v.label, stock: v.stock == null ? "" : String(v.stock) })),
  active: it.active !== false,
});

const toInt = (v) => (v === "" || v == null ? null : Math.max(0, parseInt(v, 10) || 0));

const toPayload = (f) => {
  const variants = f.variants.filter((v) => v.label.trim()).map((v) => ({ label: v.label.trim(), stock: toInt(v.stock) }));
  return {
    name: f.name.trim(), description: f.description, price: parseFloat(f.price) || 0,
    image_url: f.image_url, requires_shipping: f.requires_shipping, active: f.active,
    stock: variants.length ? null : toInt(f.stock), variants: variants.length ? variants : null,
  };
};

function VariantRows({ variants, onChange }) {
  const set = (i, patch) => onChange(variants.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  return (
    <div className="space-y-2" data-testid="merch-variants-editor">
      {variants.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input placeholder="Option (e.g. Large / Black)" value={v.label} onChange={(e) => set(i, { label: e.target.value })} className="flex-1 px-3 py-2 text-sm" data-testid={`merch-variant-label-${i}`} />
          <input type="number" min={0} placeholder="Stock ∞" value={v.stock} onChange={(e) => set(i, { stock: e.target.value })} className="w-24 px-3 py-2 text-sm" data-testid={`merch-variant-stock-${i}`} />
          <button type="button" onClick={() => onChange(variants.filter((_, j) => j !== i))} className="text-[var(--ff-red)] px-1" data-testid={`merch-variant-remove-${i}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...variants, { label: "", stock: "" }])} className="btn-ghost text-xs inline-flex items-center gap-1.5 !px-3 !py-2" data-testid="merch-variant-add-btn">
        <Plus size={12} /> Add Option
      </button>
    </div>
  );
}

function ImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${API_BASE.replace(/\/api$/, "")}${data.url}`);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div className="flex items-start gap-3">
      {value
        ? <img src={value} alt="preview" className="w-16 h-16 object-cover border border-[var(--ff-border)]" data-testid="merch-image-preview" />
        : <div className="w-16 h-16 bg-[var(--ff-bg)] border border-dashed border-[var(--ff-border)] flex items-center justify-center text-[var(--ff-text-dim)] text-xs">IMG</div>}
      <div className="flex-1 space-y-2">
        <label className="btn-ghost text-xs inline-flex items-center gap-1.5 cursor-pointer w-full justify-center">
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? "Uploading..." : "Upload Image"}
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} data-testid="merch-image-upload" />
        </label>
        <input placeholder="or paste image URL" value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-xs" data-testid="merch-image-url-input" />
      </div>
    </div>
  );
}

export default function MerchItemForm({ showId, editing, form, setForm, onSaved, onCancel }) {
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!(parseFloat(form.price) > 0)) { toast.error("Price must be greater than $0"); return; }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing) await api.put(`/admin/merch/${editing.id}`, payload);
      else await api.post(`/admin/shows/${showId}/merch`, payload);
      toast.success(editing ? "Item updated" : "Item added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="surface-card p-6 space-y-3" data-testid="merch-item-form">
      <h3 className="font-display text-2xl mb-2">{editing ? "Edit Item" : "Add Item"}</h3>
      <input required placeholder="Item name" value={form.name} onChange={(e) => set({ name: e.target.value })} className="w-full px-3 py-2" data-testid="merch-name-input" />
      <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} className="w-full px-3 py-2" data-testid="merch-description-input" />
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="label-eyebrow block mb-1">Price (USD)</label>
          <input required type="number" step="0.01" min="0.5" placeholder="25.00" value={form.price} onChange={(e) => set({ price: e.target.value })} className="w-full px-3 py-2" data-testid="merch-price-input" />
        </div>
        <div className="flex-1">
          <label className="label-eyebrow block mb-1">Type</label>
          <select value={form.requires_shipping ? "physical" : "digital"} onChange={(e) => set({ requires_shipping: e.target.value === "physical" })} className="w-full px-3 py-2 bg-[var(--ff-bg)] border border-[var(--ff-border)]" data-testid="merch-type-select">
            <option value="physical">Physical (ships)</option>
            <option value="digital">Digital</option>
          </select>
        </div>
      </div>
      <div>
        <label className="label-eyebrow block mb-2">Image</label>
        <ImageField value={form.image_url} onChange={(url) => set({ image_url: url })} />
      </div>
      <div>
        <label className="label-eyebrow block mb-2">Options / Variants <span className="normal-case tracking-normal text-[var(--ff-text-dim)]">(blank stock = unlimited)</span></label>
        <VariantRows variants={form.variants} onChange={(variants) => set({ variants })} />
      </div>
      {form.variants.length === 0 && (
        <div>
          <label className="label-eyebrow block mb-1">Stock <span className="normal-case tracking-normal text-[var(--ff-text-dim)]">(blank = unlimited)</span></label>
          <input type="number" min={0} placeholder="∞" value={form.stock} onChange={(e) => set({ stock: e.target.value })} className="w-full px-3 py-2" data-testid="merch-stock-input" />
        </div>
      )}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} data-testid="merch-active-toggle" />
        Visible in store
      </label>
      <div className="flex gap-2 pt-1">
        <button disabled={saving} className="btn-primary flex-1 justify-center" data-testid={editing ? "merch-update-btn" : "merch-create-btn"}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : editing ? <Pencil size={14} /> : <Plus size={14} />}
          {editing ? "Update Item" : "Add Item"}
        </button>
        {editing && <button type="button" onClick={onCancel} className="btn-ghost inline-flex items-center gap-1.5" data-testid="merch-cancel-edit-btn"><X size={14} />Cancel</button>}
      </div>
    </form>
  );
}
